/**
 * ProgressIndicator — 3-phase streaming progress display.
 *
 * Phase 1: THINKING — No tools dispatched yet. Shows pulsing dots.
 * Phase 2: AGENTS WORKING — Tools in flight. Shows module-level labels
 *          with running/done status, animated progress bar, and count.
 * Phase 3: COMPOSING — All tools done, waiting for text. Shows pulse.
 *
 * Key UX principle: Users never see internal tool names like
 * "epicor_get_vendor". They see module-level labels like "Querying AP data..."
 */

import { useMemo } from 'react'
import { getModuleVerb, getModuleLabel, getModuleColor } from '../../lib/tool-modules'
import { Check, Loader2, ShieldAlert } from 'lucide-react'

/**
 * @param {{ activeTools: Array<{ name: string, status: 'running' | 'done' | 'error' }> }} props
 */
export default function ProgressIndicator({ activeTools = [] }) {
  const totalTools = activeTools.length
  const doneCount = activeTools.filter(t => t.status === 'done' || t.status === 'error').length
  const awaitingApproval = activeTools.some(t => t.status === 'awaiting_approval')
  const allDone = totalTools > 0 && doneCount === totalTools && !awaitingApproval
  const hasTools = totalTools > 0

  // Phase detection
  const phase = !hasTools ? 'thinking' : allDone ? 'composing' : 'working'

  // Deduplicate by module label — group tools by their module
  const moduleGroups = useMemo(() => {
    const groups = new Map()
    for (const tool of activeTools) {
      const label = getModuleLabel(tool.name)
      const verb = getModuleVerb(tool.name)
      const colors = getModuleColor(tool.name)
      if (!groups.has(label)) {
        groups.set(label, { label, verb, colors, tools: [], allDone: true })
      }
      const group = groups.get(label)
      group.tools.push(tool)
      if (tool.status === 'running') group.allDone = false
      if (tool.status === 'awaiting_approval') { group.allDone = false; group.awaitingApproval = true }
    }
    return Array.from(groups.values())
  }, [activeTools])

  // Progress percentage
  const progressPct = totalTools > 0 ? Math.round((doneCount / totalTools) * 100) : 0

  // ── Phase 1: Thinking ──
  if (phase === 'thinking') {
    return (
      <div className="flex items-center gap-2 py-2">
        <div className="flex items-center gap-1">
          <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
          <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
          <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>
        <span className="text-sm text-gray-500 dark:text-gray-400 italic">Thinking</span>
      </div>
    )
  }

  // ── Phase 3: Composing ──
  if (phase === 'composing') {
    return (
      <div className="flex flex-col gap-2 py-2">
        {/* Show completed modules briefly */}
        <div className="flex flex-wrap gap-1.5">
          {moduleGroups.map((group) => (
            <span
              key={group.label}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium border ${group.colors.bg} ${group.colors.text} ${group.colors.border}`}
            >
              <Check size={10} className="flex-shrink-0" />
              {group.label}
            </span>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 border-2 border-amtrend-blue border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-gray-500 dark:text-gray-400 italic">Composing response...</span>
        </div>
      </div>
    )
  }

  // ── Phase 2: Agents Working ──
  return (
    <div className="flex flex-col gap-2.5 py-2">
      {/* Module status list */}
      <div className="flex flex-wrap gap-1.5">
        {moduleGroups.map((group) => (
          <span
            key={group.label}
            className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-medium border transition-all duration-300 ${group.colors.bg} ${group.colors.text} ${group.colors.border}`}
          >
            {group.allDone ? (
              <Check size={10} className="flex-shrink-0" />
            ) : group.awaitingApproval ? (
              <ShieldAlert size={10} className="flex-shrink-0 text-amber-500" />
            ) : (
              <Loader2 size={10} className="flex-shrink-0 animate-spin" />
            )}
            {group.allDone ? group.label : group.awaitingApproval ? 'Awaiting approval...' : group.verb}
          </span>
        ))}
      </div>

      {/* Progress bar + count */}
      <div className="flex items-center gap-2">
        <div className="flex-1 h-1 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden max-w-[200px]">
          <div
            className="h-full bg-amtrend-blue rounded-full transition-all duration-500 ease-out progress-bar-fill"
            style={{ width: `${progressPct}%` }}
          />
        </div>
        <span className="text-[11px] text-gray-400 dark:text-gray-500 font-medium tabular-nums">
          {doneCount} of {totalTools} agents
        </span>
      </div>
    </div>
  )
}
